// Bandeja -> Casos: los tickets (como en Zammad). Cada correo nuevo a un buzon de grupo abre uno; se asignan, cambian de
// estado y prioridad, llevan notas internas y se responden en su propio hilo de correo.
import { WhatsAppRespuesta } from '../../ui/WhatsAppRespuesta';
import { abrirChat } from '../../ui/Chat';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  aCaso,
  buscarCasos,
  type Caso,
  type CambiosCaso,
  editarCaso,
  type RawCaso,
  unirCasos,
  mensajesDeCaso,
  ESTADOS_CASO,
  type EstadoCaso,
  PRIORIDADES,
  type PrioridadCaso,
  adjuntosZammad,
  archivoZammad,
  abreEnCorreo,
  guardarRespuestaEnCaso,
} from '../../api/casos';
import { useSession } from '../../auth/session';
import { useMiembros } from '../../data/queries';
import { ago } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { Empty, ErrorBox, Pill, Skeleton } from '../../ui/bits';
import { useBuzones } from '../../ui/Buzones';
import { abrirDialogo } from '../../ui/forms';
import { toast } from '../../ui/toasts';
import { iniciarCronometro } from '../../lib/cronometro';
import { casosUnidosAqui, casoUnidoA } from '../../lib/casosUnidos';
import { EnlaceCaso } from '../../ui/EnlaceCaso';
import { OpcionCaso } from '../../ui/OpcionCaso';
import { readJSON, writeJSON } from '../../lib/storage';
import { conversacionDeCaso, hilosDeCaso, msgDeGuardado } from '../../lib/conversacionCaso';
import { useCorreoDelCaso } from './useCorreoDelCaso';
import { AvisoCopia } from '../../ui/AvisoCopias';
import { actividadDe, marcarVisto } from '../../lib/vistosCaso';
import { Hilo, useEstadosCorreo } from './Correos';
import { asuntoDeCaso } from '../../lib/correo';
import { Adjuntos } from '../../ui/AdjuntosCorreo';
import { Conversacion, juntarPorFecha, type MsgConv, notasComoMensajes } from '../../ui/Conversacion';
import { type Eleccion, type EnvioRespuesta, Respuesta } from '../../ui/Respuesta';
import { useFirma } from '../../ui/Plantillas';
import { useBuzonesVisibles } from './useHilos';
import { useBuzonesGrupo } from '../administracion/BuzonesGrupo';
import { CAMPOS_LISTA } from '../../api/vistasCaso';
import { fijarCaso, quitarCaso } from '../../lib/pestanasCaso';
import { senalDe, siguienteCaso, textoSenal } from '../../lib/senalesCaso';
import { BarraFiltros, ListaCasos, MiDia } from './CasosVistas';
import { gqlTodos } from '../../api/gql';
import { Dialog } from '../../ui/Dialog';
import { SelectorFecha } from '../../ui/SelectorFecha';
import { escribirFecha } from '../../lib/fechas';
import { refrescarCasos, useCasosSinCerrar, useDetalleCaso, useMisGrupos, useVistaElegida, useVistasCaso, useVistasVisibles } from './useVistas';
import { useAutomatizaciones } from '../administracion/Automatizaciones';
import { nuncaSeAvisa } from '../../lib/avisoCliente';
import { type CampoCambio } from '../../lib/cambiosPendientes';
import { useCambiosPendientes } from '../../lib/useCambiosPendientes';
import { BarraCambios } from '../../ui/BarraCambios';
import { type AdjuntoRecibido, enviar, subirAdjunto } from '../../api/correo';
import { SaldoEmpresa } from '../ventas/Bolsas';
import { ArticulosSugeridos } from './ArticulosSugeridos';

// Lo que se cambia desde la columna Detalles y espera a "Guardar" (D92).
type DetallesCaso = { estado: EstadoCaso; prioridad: PrioridadCaso; asignadoId: string; grupo: string };
const tonoEstado = (e: EstadoCaso) => ESTADOS_CASO.find(([v]) => v === e)?.[2];
const nombreEstado = (e: EstadoCaso) => ESTADOS_CASO.find(([v]) => v === e)?.[1] ?? e;

export function Casos() {
  const [sp, setSp] = useSearchParams();
  const casoId = sp.get('caso');
  const elegida = useVistaElegida();
  const vistasQ = useVistasCaso();
  const vistas = useVistasVisibles();
  const { puede } = useSession();
  if (casoId) return <CasoCompleto id={casoId} volver={() => (sp.delete('caso'), setSp(new URLSearchParams(sp), { replace: false }))} />;
  const titulo = elegida.tipo === 'dia' ? 'Mi día' : elegida.tipo === 'todos' ? 'Todos los casos' : elegida.tipo === 'huerfanos' ? 'Sin grupo del CRM' : elegida.vista.nombre;
  const sub =
    elegida.tipo === 'dia'
      ? `${new Date().toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })} · lo que te escribieron, lo que toca perseguir y lo nuevo de tus grupos`
      : elegida.tipo === 'guardada'
        ? 'Vista guardada'
        : elegida.tipo === 'huerfanos'
          ? 'Solo administradores'
          : 'La lista completa de tus grupos';
  return (
    <div className="casos-pag">
      <header className="casos-h">
        <h1>{titulo}</h1>
        <p>{sub}</p>
      </header>
      {/* En el celular no hay columna de vistas: se elige aqui. */}
      <select className="inp vistas-movil" value={sp.get('vista') ?? 'dia'} onChange={(e) => (sp.set('vista', e.target.value), setSp(new URLSearchParams(sp)))} aria-label="Vista">
        <option value="dia">Mi día</option>
        <option value="todos">Todos los casos</option>
        {puede('casos.sinGrupo') ? <option value="huerfanos">Sin grupo del CRM</option> : null}
        {vistas.map((v) => (
          <option key={v.id} value={v.id}>
            {v.nombre}
          </option>
        ))}
      </select>
      <BarraFiltros etiquetaTodos={elegida.tipo === 'guardada' ? 'Como la vista' : 'Sin cerrar'} />
      {vistasQ.isLoading ? <Skeleton rows={6} /> : elegida.tipo === 'dia' ? <MiDia /> : <ListaCasos elegida={elegida} />}
    </div>
  );
}

// Caso a pantalla completa (D37): reemplaza la lista; "Volver" regresa a la vista con sus filtros.
function CasoCompleto({ id, volver }: { id: string; volver: () => void }) {
  const { yo } = useSession();
  const sinCerrar = useCasosSinCerrar();
  // La lista no trae los textos largos del caso: se le juntan (useDetalleCaso) antes de mostrarlo.
  const detalle = useDetalleCaso(sinCerrar.data?.find((c) => c.id === id));
  const enLista = detalle.caso;
  const suelto = useQuery({
    queryKey: ['caso', id],
    queryFn: async () => (await gqlTodos<RawCaso>('casos', CAMPOS_LISTA, { filtro: { id: { eq: id } }, max: 1 })).map(aCaso)[0] ?? null,
    enabled: !enLista && !detalle.cargando && !sinCerrar.isLoading,
    // Un caso cerrado abierto en pantalla tambien se actualiza solo (p. ej. el cliente responde y se reabre).
    refetchInterval: 30_000,
  });
  const c = enLista ?? suelto.data ?? null;
  // Al abrirlo queda en "Abiertos ahora".
  useEffect(() => fijarCaso(yo.miembroId, id), [id, yo.miembroId]);
  if (!c) {
    if (suelto.isLoading || sinCerrar.isLoading || detalle.cargando)
      return (
        <div style={{ padding: 24 }}>
          <Skeleton rows={8} />
        </div>
      );
    return <Empty icon="inbox" titulo="No se encontró el caso" texto="Puede que se haya unido a otro o que no sea de tus grupos." />;
  }
  return <CasoDetalle key={c.id} caso={c} onVolver={volver} />;
}

function CasoDetalle({ caso: c, onVolver }: { caso: Caso; onVolver: () => void }) {
  const qc = useQueryClient();
  const { yo, puede } = useSession();
  // Sin "Reasignar casos" (D99) solo se toma un caso libre o propio, para uno mismo.
  const reasigna = puede('casos.reasignar') || !c.asignadoId || c.asignadoId === yo.miembroId;
  const navegar = useNavigate();
  const onIr = (id: string) => {
    const n = new URLSearchParams(window.location.search);
    n.set('caso', id);
    navegar(`/bandeja/casos?${n.toString()}`);
  };
  const sinCerrar = useCasosSinCerrar();
  const misGrupos = useMisGrupos();
  const sig = siguienteCaso(sinCerrar.data ?? [], c.id, yo.miembroId, misGrupos.miembro);
  const senal = senalDe(c);
  const [esperando, setEsperando] = useState(false);
  const miembros = useMiembros();
  const b = useBuzones();
  const { estados, cambiar } = useEstadosCorreo();
  const [uniendo, setUniendo] = useState(false);
  // Casos de Zammad: abren en su conversacion, que ya trae lo que llega por correo (D81); el boton deja ver el correo
  // original. La vista no cambia sola al llegar un correo (antes si, y la respuesta enviada desde el caso desaparecia).
  const [eleccionVista, setVerCorreo] = useState<boolean | null>(null);
  const verCorreo = eleccionVista ?? abreEnCorreo(c);
  // Casos unidos (de sus notas): a cual se unio este y cuales se unieron aqui.
  const unidoA = c.etiquetas.includes('unido') ? casoUnidoA(c.notas) : null;
  const unidosAqui = casosUnidosAqui(c.notas);
  // Columna de Detalles a la derecha (D41, opcion B); se recuerda si la persona la oculta.
  const [verDetalles, setVerDetalles] = useState(() => readJSON<boolean>('consola.caso.detalles', true));
  const mostrarDetalles = (v: boolean) => {
    setVerDetalles(v);
    writeJSON('consola.caso.detalles', v);
  };
  const todosGrupos = useBuzonesGrupo();
  const cuentaGrupo = (todosGrupos.data ?? []).find((g) => g.nombre === c.grupo)?.cuentaId ?? null;
  // Nuestros correos: buzones conectados, correos de los grupos y el de quien responde (nunca se les responde).
  const mios = [...new Set([...(b.data ?? []).map((x) => x.correo), ...(todosGrupos.data ?? []).map((g) => g.correo), yo.email].filter((x): x is string => !!x).map((x) => x.toLowerCase()))];
  const abiertos = useRef(new Set<string>());
  // Abrir el caso lo marca como visto hasta su ultima actividad (D42); si llega algo mientras esta abierto, tambien.
  const ultimaActividad = actividadDe(c).fecha;
  useEffect(() => {
    marcarVisto(yo.miembroId, c.id, ultimaActividad);
  }, [yo.miembroId, c.id, ultimaActividad]);

  const guardar = async (cambios: CambiosCaso, aviso?: string) => {
    const aplicar = (x: Caso) => (x.id === c.id ? { ...x, ...cambios, asunto: cambios.name ?? x.asunto, notas: cambios.notasInternas ?? x.notas } : x);
    qc.setQueryData<Caso[]>(['casos'], (xs) => xs?.map(aplicar));
    qc.setQueriesData<Caso[]>({ queryKey: ['casos-vista'] }, (xs) => xs?.map(aplicar));
    qc.setQueryData<Caso | null>(['caso', c.id], (x) => (x ? aplicar(x) : x));
    try {
      await editarCaso(c.id, cambios);
      if (aviso) toast(aviso);
    } catch (e) {
      toast((e as Error).message, 'error');
    }
    refrescarCasos(qc);
  };
  // Un caso Nuevo es uno que nadie ha abierto: al abrirlo pasa a Abierto (y sale de "Sin asignar"). Asignarlo es otra decision.
  useEffect(() => {
    if (c.estado !== 'NUEVO' || abiertos.current.has(c.id)) return;
    abiertos.current.add(c.id);
    void guardar({ estado: 'ABIERTO' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.id, c.estado]);
  const auto = useAutomatizaciones();
  const gruposCaso = useBuzonesGrupo();
  // Aviso de caso cerrado (Administracion -> Automatizaciones): lo envia la funcion casos-desde-correo de la app a los
  // pocos minutos de resolver o cerrar, desde el buzon del grupo (D90). La casilla "Avisar al cliente" lo evita: sin
  // ella, el caso se guarda con avisoCierre igual a cerradoEn y la funcion lo da por avisado.
  const grupoAviso = (gruposCaso.data ?? []).find((x) => x.nombre === c.grupo && x.cuentaId);
  const conAviso = !!auto.data?.casoResuelto?.activa && !!grupoAviso && !!c.correoCliente && (c.origen === 'CORREO' || c.origen === 'ZAMMAD') && !c.etiquetas.some((x) => x === 'historial-zammad' || x === 'unido');
  // Casos internos y remitentes que contestan solos (bucles de correo) nunca lo reciben.
  const nunca = nuncaSeAvisa(c.correoCliente, mios);
  const [eleccionAviso, setAvisar] = useState<{ caso: string; avisar: boolean } | null>(null);
  const avisar = !nunca && (eleccionAviso?.caso === c.id ? eleccionAviso.avisar : true);
  const cambiosEstado = (e: EstadoCaso) => {
    const yaCerrado = c.estado === 'RESUELTO' || c.estado === 'CERRADO';
    const cerrando = e === 'RESUELTO' || e === 'CERRADO';
    const ahora = new Date().toISOString();
    const sinAviso = cerrando && !yaCerrado && conAviso && !avisar;
    return {
      cerrando,
      cambios: { estado: e, cerradoEn: cerrando ? ahora : null, ...(sinAviso ? { avisoCierre: ahora } : {}), ...(e !== 'PENDIENTE' ? { seguimientoEn: null } : {}) } satisfies CambiosCaso,
      aviso: `Caso #${c.numero}: ${nombreEstado(e).toLowerCase()}${sinAviso && !nunca ? ' (sin avisar al cliente)' : ''}.`,
    };
  };
  // "Resolver" de arriba: se aplica al momento (lo que quedara pendiente del estado en Detalles se olvida).
  const estado = (e: EstadoCaso) => {
    const r = cambiosEstado(e);
    setDet((p) => ({ caso: c.id, v: { ...(p.caso === c.id ? p.v : {}), estado: undefined } }));
    void guardar(r.cambios, r.aviso);
    // Resuelto o cerrado ya no hace falta tenerlo a la mano.
    if (r.cerrando) quitarCaso(yo.miembroId, c.id);
  };
  // Detalles (estado, prioridad, asignado y grupo) ya no se guardan al elegir: quedan pendientes en la barra de cambios
  // hasta "Guardar" (o Ctrl+S); antes cambiar el estado a Cerrado cerraba el caso al instante (D92).
  const ponerDet = (x: Partial<DetallesCaso>) => setDet((p) => ({ caso: c.id, v: { ...(p.caso === c.id ? p.v : {}), ...x } }));
  const guardadoDet = useMemo<DetallesCaso>(() => ({ estado: c.estado, prioridad: c.prioridad, asignadoId: c.asignadoId ?? '', grupo: c.grupo }), [c.estado, c.prioridad, c.asignadoId, c.grupo]);
  const [det, setDet] = useState<{ caso: string; v: Partial<DetallesCaso> }>({ caso: c.id, v: {} });
  const pendientes = det.caso === c.id ? Object.fromEntries(Object.entries(det.v).filter(([, x]) => x !== undefined)) : {};
  const vDet: DetallesCaso = { ...guardadoDet, ...pendientes };
  const camposDet = useMemo<CampoCambio<DetallesCaso>[]>(
    () => [
      { id: 'estado', t: 'Estado', fmt: (x) => nombreEstado(x as EstadoCaso) },
      { id: 'prioridad', t: 'Prioridad', fmt: (x) => PRIORIDADES.find(([v]) => v === x)?.[1] ?? String(x) },
      { id: 'asignadoId', t: 'Asignado', fmt: (x) => (miembros.data ?? []).find((m) => m.id === x)?.nombre ?? 'Sin asignar' },
      { id: 'grupo', t: 'Grupo' },
    ],
    [miembros.data],
  );
  const guardarDetalles = async () => {
    const cambios: CambiosCaso = {};
    const avisos: string[] = [];
    let cerrando = false;
    if (vDet.estado !== c.estado) {
      const r = cambiosEstado(vDet.estado);
      Object.assign(cambios, r.cambios);
      avisos.push(r.aviso);
      cerrando = r.cerrando;
    }
    if (vDet.prioridad !== c.prioridad) cambios.prioridad = vDet.prioridad;
    if (vDet.asignadoId !== (c.asignadoId ?? '')) {
      cambios.asignadoId = vDet.asignadoId || null;
      if (c.estado === 'NUEVO' && vDet.asignadoId && !cambios.estado) cambios.estado = 'ABIERTO';
      avisos.push('Asignado.');
    }
    if (vDet.grupo !== c.grupo) {
      cambios.grupo = vDet.grupo;
      avisos.push(`Pasó al grupo ${vDet.grupo}.`);
    }
    if (!Object.keys(cambios).length) return true;
    await guardar(cambios, avisos.join(' ') || 'Cambios guardados.');
    setDet({ caso: c.id, v: {} });
    if (cerrando) quitarCaso(yo.miembroId, c.id);
    return true;
  };
  const cambiosDet = useCambiosPendientes<DetallesCaso>({
    titulo: `Caso #${c.numero}`,
    inicial: guardadoDet,
    actual: vDet,
    campos: camposDet,
    poner: (x) => ponerDet(x),
    guardar: guardarDetalles,
  });
  // Esperar al cliente: queda "Pendiente del cliente" y con fecha para volver a escribirle (aviso ese dia).
  const esperar = (dias: number | string) => {
    const f = typeof dias === 'number' ? new Date() : new Date(`${dias}T09:00:00`);
    if (typeof dias === 'number') {
      f.setDate(f.getDate() + dias);
      f.setHours(9, 0, 0, 0);
    }
    setEsperando(false);
    void guardar(
      { estado: 'PENDIENTE', seguimientoEn: f.toISOString(), cerradoEn: null, ...(!c.asignadoId && yo.miembroId ? { asignadoId: yo.miembroId } : {}) },
      `Esperando al cliente. Te aviso el ${f.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}.`,
    );
  };
  const tomar = () => guardar({ asignadoId: yo.miembroId ?? null, ...(c.estado === 'NUEVO' ? { estado: 'ABIERTO' as const } : {}) }, 'Ahora es tuyo.');
  // Nota interna del caso: se escribe en el cuadro de respuesta ("Nota") y se ve en la conversacion (D41).
  const agregarNota = async (texto: string) => {
    if (!texto.trim()) return;
    await guardar({ notasInternas: [...c.notas, { autor: yo.nombre, fecha: new Date().toISOString(), texto: texto.trim() }] }, 'Nota interna guardada.');
  };

  return (
    <div className="ch caso-completo">
      <header className="caso-top">
        <div className="caso-barra">
          <button className="btn sm" onClick={onVolver}>
            <Icon name="chevLeft" /> Volver
          </button>
          <span className="caso-num">#{c.numero}</span>
          <Pill tone={tonoEstado(c.estado)}>{nombreEstado(c.estado)}</Pill>
          {senal ? (
            <span className={`senal s-${senal}`}>
              <span className="punto" aria-hidden="true" />
              {textoSenal(c, senal)}
            </span>
          ) : null}
          <div className="caso-tit">
            <TituloCaso
              titulo={c.asunto}
              guardar={(t) => guardar({ name: t, notasInternas: [...c.notas, { autor: yo.nombre, fecha: new Date().toISOString(), texto: `Título cambiado: "${c.asunto}" → "${t}".` }] }, 'Título del caso cambiado. Los correos conservan su asunto.')}
            />
            <p className="sub">
              {c.persona ? <Link to={`/personas/${c.persona.id}`}>{c.persona.nombre}</Link> : c.correoCliente || 'Sin cliente'}
              {c.empresa ? (
                <>
                  {' · '}
                  <Link to={`/empresas/${c.empresa.id}`}>{c.empresa.nombre}</Link>
                </>
              ) : null}
              {` · ${c.grupo || 'Sin grupo'} · abierto ${ago(c.creado)}`}
              {c.estado === 'PENDIENTE' && c.seguimientoEn ? ` · seguimiento el ${new Date(c.seguimientoEn).toLocaleDateString('es-CO', { day: 'numeric', month: 'long' })}` : ''}
              {!c.asignadoId && c.duenoZammad ? ` · en Zammad lo tiene ${c.duenoZammad}` : ''}
            </p>
          </div>
          <button className="btn sm" type="button" onClick={() => abrirChat(`caso:${c.id}`, `#${c.numero ?? ''} ${c.asunto}`)} title="Hablar del caso con el equipo (chat interno)">
            <Icon name="reply" /> Equipo
          </button>
          {c.asignadoId !== yo.miembroId ? (
            <button className="btn sm" onClick={() => void tomar()}>
              Lo tomo
            </button>
          ) : null}
          <div className="esperar">
            <button className="btn sm" aria-expanded={esperando} onClick={() => setEsperando(!esperando)}>
              <Icon name="clock" /> Esperar al cliente
            </button>
            {esperando ? (
              <div className="esperar-op" role="group" aria-label="Volver a escribirle">
                <span>Volver a escribirle:</span>
                <button className="btn sm" onClick={() => esperar(1)}>
                  Mañana
                </button>
                <button className="btn sm" onClick={() => esperar(3)}>
                  En 3 días
                </button>
                <button className="btn sm" onClick={() => esperar(7)}>
                  En 1 semana
                </button>
                <label>
                  Otra fecha
                  <SelectorFecha value="" min={escribirFecha(new Date())} onChange={(x) => x && esperar(x)} placeholder="Elegir día" etiqueta="Volver a escribirle" />
                </label>
              </div>
            ) : null}
          </div>
          {c.estado !== 'RESUELTO' && c.estado !== 'CERRADO' ? (
            <>
              <button className="btn sm" onClick={() => estado('RESUELTO')}>
                <Icon name="check" /> Resolver
              </button>
              {conAviso ? (
                <label
                  className="avisar-cliente"
                  title={nunca ? `A ${c.correoCliente} nunca se le envía el aviso: es un correo nuestro o automático.` : `Al resolver, ${c.correoCliente} recibe el aviso de caso cerrado. Quítala en casos internos o si ese correo contesta solo.`}
                >
                  <input type="checkbox" checked={avisar} disabled={nunca} onChange={(e) => setAvisar({ caso: c.id, avisar: e.target.checked })} /> Avisar al cliente
                </label>
              ) : null}
            </>
          ) : null}
          {sig ? (
            <button className="btn sm primary" onClick={() => onIr(sig.id)} title={sig.asunto}>
              Siguiente: {sig.empresa?.nombre || sig.persona?.nombre || `#${sig.numero}`} <Icon name="chevRight" />
            </button>
          ) : null}
        </div>
      </header>
      {c.resumenIA ? (
        <section className="caso-ia" aria-label="Lo que entendió el asistente">
          <Icon name="spark" />
          <div>
            <p>
              <b>En resumen:</b> {c.resumenIA}
            </p>
            {c.siguientePasoIA ? (
              <p>
                <b>Lo que sigue:</b> {c.siguientePasoIA}
              </p>
            ) : null}
            {c.respuestaIA ? (
              <button className="btn sm" onClick={() => void navigator.clipboard?.writeText(c.respuestaIA).then(() => toast('Respuesta sugerida copiada: pégala en la respuesta.'))}>
                Copiar respuesta sugerida
              </button>
            ) : null}
          </div>
        </section>
      ) : null}
      {unidoA ? (
        <section className="caso-unido-a" aria-label="Caso unido">
          <Icon name="link" />
          <span>
            Este caso se unió al <EnlaceCaso numero={unidoA.numero} />
            {unidoA.asunto ? <span className="sb"> · {unidoA.asunto}</span> : null}. Su conversación sigue allá.
          </span>
          <EnlaceCaso numero={unidoA.numero}>
            <span className="btn sm primary">Abrir el caso</span>
          </EnlaceCaso>
        </section>
      ) : null}
      {/* Lo que escribi con el cliente desde mi buzon y se copio a este caso: aviso con Deshacer (24 h). */}
      <AvisoCopia casoId={c.id} />
      <div className="caso-cuerpo">
        <div className="caso-main">
          {/* Casos de Zammad: su historial (con notas) mas el correo que llego despues a sus hilos, en una sola conversacion. */}
          <div className="caso-hilo">
            {c.hiloId && (c.origen !== 'ZAMMAD' || verCorreo) ? (
              <Hilo id={c.hiloId} unidos={c.hilosUnidos} casoId={c.id} mios={mios} estado={estados.get(c.hiloId)} cambiar={cambiar} onVolver={onVolver} enCaso desdeCuentaId={cuentaGrupo} notas={c.notas} onNota={agregarNota} caso={{ numero: c.numero, titulo: c.asunto }} />
            ) : (
              <Historial caso={c} mios={mios} onNota={agregarNota} />
            )}
          </div>
          {/* Los correos unidos a este caso (D50) ya van dentro de la conversacion, en su fecha (lib/conversacionCaso). */}
        </div>
        {verDetalles ? (
          <aside className="caso-det" aria-label="Detalles del caso">
            <BarraCambios />
            <div className="caso-det-h">
              <b>Detalles</b>
              <button className="linkish" onClick={() => mostrarDetalles(false)}>
                Ocultar »
              </button>
            </div>
            <label className={cambiosDet.clase('estado')}>
              Estado
              <select value={vDet.estado} onChange={(e) => ponerDet({ estado: e.target.value as EstadoCaso })}>
                {ESTADOS_CASO.map(([v, t]) => (
                  <option key={v} value={v}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className={cambiosDet.clase('prioridad')}>
              Prioridad
              <select value={vDet.prioridad} onChange={(e) => ponerDet({ prioridad: e.target.value as PrioridadCaso })}>
                {PRIORIDADES.map(([v, t]) => (
                  <option key={v} value={v}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className={cambiosDet.clase('asignadoId')}>
              Asignado
              <select value={vDet.asignadoId} onChange={(e) => ponerDet({ asignadoId: e.target.value })} disabled={!reasigna} title={reasigna ? undefined : 'Tu rol no reasigna casos de otras personas'}>
                <option value="">Sin asignar</option>
                {(miembros.data ?? []).filter((m) => puede('casos.reasignar') || m.id === yo.miembroId || m.id === c.asignadoId).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nombre}
                  </option>
                ))}
              </select>
            </label>
            <label className={cambiosDet.clase('grupo')}>
              Grupo
              <select value={vDet.grupo} onChange={(e) => ponerDet({ grupo: e.target.value })}>
                {[...new Set([c.grupo, ...(todosGrupos.data ?? []).filter((g) => g.activo).map((g) => g.nombre)])]
                  .filter(Boolean)
                  .sort((a, b) => a.localeCompare(b))
                  .map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
              </select>
            </label>
            <EtiquetasCaso caso={c} guardar={guardar} />
            <ArticulosSugeridos titulo={c.asunto} etiquetas={c.etiquetas} />
            {unidosAqui.length ? (
              <div className="caso-unidos-aqui">
                <span className="caso-etq-t">Casos unidos aquí</span>
                {unidosAqui.map((u) => (
                  <span key={u.numero} title={u.asunto}>
                    <EnlaceCaso numero={u.numero} /> <span className="sb">{u.asunto}</span>
                  </span>
                ))}
              </div>
            ) : null}
            <div className="caso-det-acc">
              <button className="btn sm" onClick={() => (iniciarCronometro({ etiqueta: `Caso #${c.numero} ${c.asunto}`, casoId: c.id }), toast('Cronómetro en marcha: lo ves arriba.'))}>
                <Icon name="play" /> Cronómetro
              </button>
              <button className="btn sm" onClick={() => abrirDialogo({ tipo: 'horas', casoId: c.id, descripcion: `Caso #${c.numero} ${c.asunto}` })}>
                <Icon name="clock" /> Horas
              </button>
              <SaldoEmpresa empresaId={c.empresaId} />
              <button className="btn sm" onClick={() => setUniendo(true)} title="Juntar este caso con otro (como Merge en Zammad)">
                <Icon name="link" /> Unir
              </button>
            </div>
            {c.hiloId && c.origen === 'ZAMMAD' ? (
              <button className="linkish caso-correo" onClick={() => setVerCorreo(!verCorreo)}>
                {verCorreo ? 'Volver a la conversación del caso' : 'Ver el correo original (con adjuntos)'}
              </button>
            ) : null}
            <div className="caso-det-cli">
              <b>Cliente</b>
              {c.empresa ? <Link to={`/empresas/${c.empresa.id}`}>{c.empresa.nombre}</Link> : null}
              {c.persona ? <Link to={`/personas/${c.persona.id}`}>{c.persona.nombre}</Link> : null}
              <span>{c.correoCliente || 'Sin correo'}</span>
            </div>
          </aside>
        ) : (
          <button className="caso-det-ver" onClick={() => mostrarDetalles(true)} title="Mostrar los detalles del caso" aria-label="Mostrar los detalles del caso">
            <Icon name="chevLeft" /> Detalles
          </button>
        )}
      </div>
      {uniendo ? <UnirDialog caso={c} onClose={() => setUniendo(false)} onUnido={onIr} /> : null}
    </div>
  );
}

// Etiquetas del caso (D44), en Detalles: con ellas el caso entra en las vistas que las piden (por ejemplo "Proyectos
// cotizados de desarrollo"). En un caso de Zammad se ponen o quitan tambien en el ticket, para que queden en ambos y la
// sincronizacion no las deshaga. Sugiere las que usan las vistas y los casos abiertos.
function EtiquetasCaso({ caso: c, guardar }: { caso: Caso; guardar: (cambios: CambiosCaso, aviso?: string) => Promise<void> }) {
  const [nueva, setNueva] = useState('');
  const vistas = useVistasCaso();
  const sinCerrar = useCasosSinCerrar();
  const sugeridas = [...new Set([...(vistas.data ?? []).flatMap((v) => v.etiquetas), ...(sinCerrar.data ?? []).flatMap((x) => x.etiquetas)])]
    .filter((e) => e && !c.etiquetas.includes(e))
    .sort((a, b) => a.localeCompare(b));
  // Zammad desligado (D81): las etiquetas quedan solo en el CRM.
  const cambiar = async (etiqueta: string, quitar: boolean) => {
    const t = etiqueta.trim();
    if (!t || (!quitar && c.etiquetas.includes(t))) return;
    await guardar({ etiquetas: quitar ? c.etiquetas.filter((x) => x !== t) : [...c.etiquetas, t] }, quitar ? `Se quitó la etiqueta "${t}".` : `Etiqueta "${t}" agregada.`);
  };
  return (
    <div className="caso-etq">
      <span className="caso-etq-t">Etiquetas</span>
      <div className="caso-etq-lista">
        {c.etiquetas.length ? null : <span className="note">Sin etiquetas</span>}
        {c.etiquetas.map((e) => (
          <span key={e} className="chip">
            {e}
            <button type="button" aria-label={`Quitar la etiqueta ${e}`} onClick={() => void cambiar(e, true)}>
              <Icon name="x" size={11} />
            </button>
          </span>
        ))}
      </div>
      <form className="caso-etq-nueva" onSubmit={(ev) => (ev.preventDefault(), void cambiar(nueva, false), setNueva(''))}>
        <input className="inp" list={`etq-${c.id}`} value={nueva} onChange={(ev) => setNueva(ev.target.value)} placeholder="Agregar etiqueta…" aria-label="Agregar etiqueta" />
        <datalist id={`etq-${c.id}`}>
          {sugeridas.map((e) => (
            <option key={e} value={e} />
          ))}
        </datalist>
        <button className="btn sm" disabled={!nueva.trim()}>
          <Icon name="plus" />
        </button>
      </form>
    </div>
  );
}

// Unir un caso con otro (como "Merge" en Zammad): el caso que se abrio se junta DENTRO del que se elige.
function UnirDialog({ caso: c, onClose, onUnido }: { caso: Caso; onClose: () => void; onUnido: (id: string) => void }) {
  const qc = useQueryClient();
  const { yo } = useSession();
  const [q, setQ] = useState('');
  const [destino, setDestino] = useState<Caso | null>(null);
  const [uniendo, setUniendo] = useState(false);
  const r = useQuery({ queryKey: ['buscar-casos', q], queryFn: () => buscarCasos(q), enabled: q.trim().length >= 2, staleTime: 10_000 });
  const unir = async () => {
    if (!destino) return;
    setUniendo(true);
    try {
      await unirCasos(c, destino, yo.nombre);
      toast(`Caso #${c.numero} unido al #${destino.numero}.`);
      refrescarCasos(qc);
      onUnido(destino.id);
      onClose();
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setUniendo(false);
    }
  };
  return (
    <Dialog titulo={`Unir el caso #${c.numero}`} subtitulo="Sus mensajes, correos y horas pasan al caso que elijas; este queda cerrado con una nota." onClose={onClose} width={560}>
      <div className="form">
        <label>
          Buscar el caso destino (número, asunto o correo)
          <input id="unir-q" className="inp" value={q} onChange={(e) => (setQ(e.target.value), setDestino(null))} placeholder="#20260810620019 o Ariza" autoFocus />
        </label>
        <div className="unir-res">
          {r.isFetching && !r.data ? <p className="sub">Buscando…</p> : null}
          {r.data && !r.data.filter((x) => x.id !== c.id).length ? <p className="sub">No encontré casos con eso.</p> : null}
          {(r.data ?? [])
            .filter((x) => x.id !== c.id)
            .map((x) => (
              <OpcionCaso key={x.id} caso={x} elegido={destino?.id === x.id} onElegir={() => setDestino(x)} />
            ))}
        </div>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn primary" disabled={!destino || uniendo} onClick={unir}>
            {uniendo ? 'Uniendo…' : destino ? `Unir al #${destino.numero}` : 'Elige un caso'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

// Conversacion de un caso que vino de Zammad (o se abrio sin correo): sus mensajes guardados y el cuadro de respuesta.
// La respuesta sale por correo desde el buzon del grupo del caso, con [Ticket#N] en el asunto para que lo que conteste
// el cliente vuelva a este caso; la nota interna queda en el caso. Zammad ya no responde (desligado, D81).
function Historial({ caso: c, mios, onNota }: { caso: Caso; mios: string[]; onNota: (texto: string) => Promise<void> }) {
  const qc = useQueryClient();
  const r = useQuery({ queryKey: ['mensajes-caso', c.id], queryFn: () => mensajesDeCaso(c.id), staleTime: 30_000, refetchInterval: 60_000 });
  const grupos = useBuzonesGrupo();
  const visibles = useBuzonesVisibles();
  const [sel, setSel] = useState<Eleccion>({ base: null, modo: 'responder' });
  const g = (grupos.data ?? []).find((x) => x.nombre === c.grupo);
  const deZammad = c.origen === 'ZAMMAD' && !!c.zammadId;
  const buzon = (visibles.data ?? []).find((x) => x.id === g?.cuentaId) ?? visibles.data?.[0];
  const firma = useFirma(buzon?.correo);
  // Adjuntos de los articulos de Zammad (D42, desde el bucket por el servicio de correo, D88): una sola consulta por
  // caso y solo cuando ya se ven sus mensajes y alguno vino de Zammad. No cambian nunca: no se vuelven a pedir.
  const conZammad = deZammad && (r.data ?? []).some((m) => !!m.zammadId);
  const adj = useQuery({ queryKey: ['adjuntos-zammad', c.id], queryFn: () => adjuntosZammad(c.id), enabled: conZammad, staleTime: Infinity, gcTime: 30 * 60_000, retry: 1 });
  const listaDe = (m: MsgConv) => (m.zammadId ? adj.data?.adjuntos[String(m.zammadId)] : undefined)?.map((a) => ({ parte: String(a.id), nombre: a.nombre, tipo: a.tipo, tamano: a.tamano, enLinea: a.enLinea, cid: a.cid }));
  // Imagenes pegadas en el correo: van dentro del texto, donde Zammad dejo su marca.
  const imagenesDeMsg = (m: MsgConv) => {
    const lista = listaDe(m);
    return lista?.length ? { lista, traer: (a: AdjuntoRecibido) => archivoZammad(c.id, m.zammadId!, Number(a.parte)), clave: `z${m.zammadId}` } : null;
  };
  const adjuntosDeMsg = (m: MsgConv, visible: string, todo: string) => {
    const lista = listaDe(m);
    if (!lista?.length) return null;
    return (
      <Adjuntos
        clave={`z${m.zammadId}`}
        lista={lista}
        traer={(a) => archivoZammad(c.id, m.zammadId!, Number(a.parte))}
        texto={todo}
        visible={visible}
      />
    );
  };
  // Lo que llego por correo a sus hilos despues de desligar Zammad (y el de los casos unidos) va en la misma
  // conversacion: antes no se veia aqui y al llegar algo la vista cambiaba sola al correo, donde no estaba lo enviado
  // desde el caso (caso #20261021620016).
  const correo = useCorreoDelCaso(hilosDeCaso(c), null, hilosDeCaso(c).length ? c.id : null);
  const conv = conversacionDeCaso({
    origen: c.origen,
    hiloId: c.hiloId,
    correo: correo.correo,
    guardados: (r.data ?? []).map((m) => msgDeGuardado(m, m.zammadId ? adj.data?.textos[String(m.zammadId)] : undefined)),
    mios,
    ocultos: correo.ocultos,
  });
  const visiblesConv = conv.filter((m) => !m.interno && !m.oculto);
  const eleccion: Eleccion = { base: sel.base ?? visiblesConv[visiblesConv.length - 1] ?? null, modo: sel.modo, abrir: sel.abrir };

  const responder = async (e: EnvioRespuesta) => {
    const reenvio = e.modo === 'reenviar';
    const citado =
      reenvio && e.base
        ? `\n\n---------- Mensaje reenviado ----------\nDe: ${e.base.deNombre} <${e.base.de}>\nFecha: ${new Date(e.base.fecha).toLocaleString('es-CO')}\nAsunto: ${e.base.asunto}\n\n${e.base.texto.replace(/\[cid:[^\]]+\]/g, '[imagen]').trim()}`
        : '';
    if (e.modo === 'nota') {
      await onNota(e.texto.trim());
      return;
    }
    if (!buzon) throw new Error('No tienes un buzón desde el cual responder.');
    const adjuntos = await Promise.all(e.adjuntos.map(subirAdjunto));
    // Si se responde a un correo del buzon, la respuesta sigue en su hilo.
    await enviar({ cuentaId: buzon.id, para: e.para, cc: e.cc, cco: e.cco, asunto: e.asunto, texto: `${e.texto}${citado}`, firma, adjuntos, responderA: reenvio ? undefined : (e.base?.headerMessageId ?? undefined) });
    // Queda en la conversacion del caso enseguida (antes la traia Zammad). Si falla, el correo ya salio: solo se avisa.
    // Con la firma con la que salio: sin ella, en el caso parecia enviado sin firma (caso #20261023620033).
    const nombres = e.adjuntos.map((a) => a.name).filter(Boolean);
    await guardarRespuestaEnCaso(c.id, { asunto: e.asunto, de: buzon.correo, para: e.para, cc: e.cc, texto: `${e.texto.trim()}${firma ? `\n\n${firma.trim()}` : ''}${nombres.length ? `\n\n[Adjuntos: ${nombres.join(', ')}]` : ''}${citado}` }).catch((err: Error) =>
      toast(`La respuesta salió, pero no quedó en la conversación del caso: ${err.message}`, 'error'),
    );
    toast(reenvio ? 'Correo reenviado.' : 'Respuesta enviada.');
    setSel({ base: null, modo: 'responder' });
    void qc.invalidateQueries({ queryKey: ['mensajes-caso', c.id] });
    refrescarCasos(qc);
  };

  if (r.isLoading || (correo.cargando && !correo.correo.length)) return <Skeleton rows={6} />;
  if (r.error) return <ErrorBox error={r.error} onRetry={() => r.refetch()} />;
  return (
    <div className="ch">
      <div className="ch-ms">
        {conv.length || c.notas.length ? (
          <Conversacion
            mensajes={juntarPorFecha(conv, notasComoMensajes(c.notas))}
            mios={mios}
            elegido={sel.base?.id}
            onAccion={(m, modo) => setSel({ base: m, modo, abrir: Date.now() })}
            adjuntos={adjuntosDeMsg}
            imagenes={imagenesDeMsg}
          />
        ) : (
          <Empty icon="mail" titulo="Sin mensajes" texto="Este caso se abrió a mano o desde una llamada." />
        )}
      </div>
      {/* WhatsApp (D73): la respuesta sale por WhatsApp, con la ventana de 24 h y plantillas aprobadas. */}
      {c.origen === 'WHATSAPP' ? <WhatsAppRespuesta casoId={c.id} ultimoCliente={c.ultimoCliente} conConversacion={!!c.whatsappConversacion} alEnviar={() => refrescarCasos(qc)} /> : null}
      {/* Siempre se puede responder o dejar una nota (D41). */}
      <Respuesta
        key={`caso:${c.id}`}
        conversacion={`caso:${c.id}`}
        mios={mios}
        sel={eleccion}
        setSel={setSel}
        conNota
        avisoNota="Solo la ve el equipo."
        desde={buzon ? `Sale desde ${buzon.correo}${firma ? ' · con tu firma' : ''}` : 'Sin buzón para responder'}
        paraInicial={c.correoCliente ? [c.correoCliente] : []}
        datos={{ 'contacto.nombre': (eleccion.base?.deNombre ?? '').split(/\s+/)[0], 'contacto.apellido': (eleccion.base?.deNombre ?? '').split(/\s+/).slice(1).join(' ') }}
        enviar={responder}
        asuntoDe={(base, modo) => asuntoDeCaso(base?.asunto || conv.find((m) => !m.interno && !m.oculto)?.asunto || '', c.numero, modo, c.asunto)}
      />
    </div>
  );
}

// Titulo del caso con un lapiz para cambiarlo (D51). Es solo el nombre del caso en el CRM: el asunto de los correos
// que se envian sale del correo al que se responde y se puede cambiar en la respuesta.
function TituloCaso({ titulo, guardar }: { titulo: string; guardar: (t: string) => Promise<void> }) {
  const [editando, setEditando] = useState(false);
  const [t, setT] = useState(titulo);
  const listo = () => {
    const nuevo = t.trim();
    setEditando(false);
    if (nuevo && nuevo !== titulo) void guardar(nuevo.slice(0, 250));
    else setT(titulo);
  };
  if (editando)
    return (
      <input
        className="inp caso-titulo-inp"
        value={t}
        autoFocus
        onChange={(e) => setT(e.target.value)}
        onBlur={listo}
        onKeyDown={(e) => (e.key === 'Enter' ? listo() : e.key === 'Escape' ? (setT(titulo), setEditando(false)) : undefined)}
        aria-label="Título del caso"
      />
    );
  return (
    <h2 className="caso-titulo" title={`${titulo} (clic en el lápiz para cambiar el título)`}>
      {titulo}{' '}
      <button type="button" className="linkish caso-titulo-ed" onClick={() => (setT(titulo), setEditando(true))} aria-label="Cambiar el título del caso">
        <Icon name="edit" size={13} />
      </button>
    </h2>
  );
}
